<script lang="ts">
    import { type Readable, type Writable, derived as sderived, readable, writable } from 'svelte/store'
    import { slide } from 'svelte/transition'

    import Icon from '@iconify/svelte'
    import type { Editor } from '@tiptap/core'
    import { orderBy } from 'lodash-es'
    import { DateTime } from 'luxon'

    import type { HydriaDocument, IAttachment, IAttachmentAltFormat, IImage } from 'hydria/HydriaDocument'
    import { simpleImageAlts } from 'hydria/attachment_helpers'
    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { session } from 'hydria/services/session'
    import { makeSortString } from 'hydria/utils/strings'

    // import Dropzone from '../Dropzone.svelte'
    import Dropzone from 'svelte-file-dropzone'
    import HumanizedBytes from '../HumanizedBytes.svelte'
    import Modal from '../Modal.svelte'
    import { selectSrc } from '../component_helpers'

    interface Props {
        editor: Editor
        Document?: HydriaDocument | undefined
        previewName?: string

        close: () => void
    }

    let { editor, Document = undefined, previewName = 'admin', close }: Props = $props()

    let images: Readable<IImage[]>
    let nonImages: Readable<IAttachment[]>

    let allowedImageMimetypes: Writable<Set<string>>
    let allowedBinaryMimetypes: Writable<Set<string>>

    if (Document) {
        images = sderived(Document.attachedImages, ($atts) => $atts)
        nonImages = sderived(Document.attachedBinaries, ($atts) => $atts)

        allowedImageMimetypes = Document.allowedImageMimetypes
        allowedBinaryMimetypes = Document.allowedBinaryMimetypes
    } else {
        images = readable([])
        nonImages = readable([])

        allowedImageMimetypes = writable(new Set())
        allowedBinaryMimetypes = writable(new Set())
    }

    interface LImage extends IImage {
        _type: 'image'
    }
    interface LBinary extends IAttachment {
        _type: 'binary'
    }

    const current = editor.getAttributes('link')

    let linkUri = $state('')
    let linkTarget: string | undefined = $state(undefined)
    let linkButton: boolean = $state()
    let linkButtonSize: 'sm' | 'lg' | null = $state()
    let linkButtonOutline: boolean = $state()
    let linkButtonColor: 'primary' | 'secondary' = $state()

    let linkOpenImage: string | null = $state(null)

    if (current) {
        linkUri = current.href
        linkTarget = current.target

        const classes = current.class ? current.class.split(' ') : []
        if (classes.includes('btn-sm')) linkButtonSize = 'sm'
        else if (classes.includes('btn-lg')) linkButtonSize = 'lg'
        else linkButtonSize = null

        if (classes.includes('btn-primary')) {
            linkButton = true
            linkButtonColor = 'primary'
            linkButtonOutline = false
        } else if (classes.includes('btn-outline-primary')) {
            linkButton = true
            linkButtonColor = 'primary'
            linkButtonOutline = true
        } else if (classes.includes('btn-outline-secondary')) {
            linkButton = true
            linkButtonColor = 'secondary'
            linkButtonOutline = false
        } else if (classes.includes('btn-outline-secondary')) {
            linkButton = true
            linkButtonColor = 'secondary'
            linkButtonOutline = true
        } else {
            linkButton = false
            linkButtonColor = 'primary'
            linkButtonOutline = false
        }
    } else {
        linkUri = ''
        linkTarget = undefined
        linkButton = false
        linkButtonSize = null
        linkButtonOutline = false
        linkButtonColor = 'primary'
    }

    function applyLink() {
        const lnk = { href: linkUri, target: linkTarget ? linkTarget : '', class: '' }
        if (linkTarget != null) lnk.target = linkTarget

        const classes: string[] = []
        if (linkButton) {
            classes.push('btn')
            if (linkButtonSize != null) classes.push(`btn-${linkButtonSize}`)
            if (linkButtonOutline) classes.push(`btn-outline-${linkButtonColor}`)
            else classes.push(`btn-${linkButtonColor}`)
        }
        lnk.class = classes.join(' ')

        editor.commands.setLink(lnk)
        close()
    }

    // #############################################################  Upload stuff

    interface LUpload {
        _type: 'upload'
        file: File
        progress: number
        started: DateTime
        ended?: DateTime
        status: 'uploading' | 'aborted' | 'finished' | 'error'
        xhr: XMLHttpRequest
        // formElement: HTMLFormElement
        // formData: FormData
        previewImage?: string
    }
    let uploads: Writable<LUpload[]> = writable([])
    let imagesAndUploads: Readable<Array<LUpload | LImage>> = sderived([images, uploads], ([$images, $uploads]) => {
        return orderBy(
            [
                ...$images.map((i) => {
                    ;(i as LImage)._type = 'image'
                    return i as LImage
                }),
                ...$uploads.filter((i) => {
                    return $allowedImageMimetypes.has(i.file.type)
                }),
            ],
            [
                (i: LImage | LUpload) => {
                    return makeSortString(i._type === 'upload' ? i.file.name : i.filename)
                },
            ],
            ['asc'],
        )
    })
    let binariesAndUploads: Readable<Array<LUpload | LBinary>> = sderived([nonImages, uploads], ([$binaries, $uploads]) => {
        return orderBy(
            [
                ...$binaries.map((i) => {
                    ;(i as LBinary)._type = 'binary'
                    return i as LBinary
                }),
                ...$uploads.filter((i) => {
                    return !$allowedImageMimetypes.has(i.file.type)
                }),
            ],
            [
                (i: LBinary | LUpload) => {
                    return makeSortString(i._type === 'upload' ? i.file.name : i.filename)
                },
            ],
            ['asc'],
        )
    })

    function setupFileUpload(file: File) {
        // const formElement = document.createElement('form')
        const upload: LUpload = {
            _type: 'upload',
            file: file,
            progress: 0,
            status: 'uploading',
            started: DateTime.now(),
            xhr: new XMLHttpRequest(),
            // formElement: formElement,
            // formData: new FormData(formElement),
            previewImage: undefined,
        }
        uploads.update(($uploads) => {
            $uploads.push(upload)
            return $uploads
        })
        const formData = new FormData()
        formData.append('file', file)

        const xhr = upload.xhr

        if (['image/jpeg', 'image/avif', 'image/webp', 'image/svg+xml', 'image/png'].includes(file.type)) {
            const reader = new FileReader()
            reader.onload = (e) => {
                upload.previewImage = (e.target as any).result as string
                // To trigger reactivity :|
                uploads.update(($uploads) => $uploads)
            }
            reader.readAsDataURL(file)
        }

        xhr.onreadystatechange = () => {
            if (xhr.readyState == XMLHttpRequest.DONE) {
                let resp: any = {
                    status: xhr.status,
                    data: null,
                }
                if (xhr.responseText) {
                    try {
                        resp.data = JSON.parse(xhr.responseText)
                    } catch (e) {}
                }

                upload.ended = DateTime.now()

                if (resp.status !== 200) {
                    upload.status = 'error'
                } else {
                    upload.status = 'finished'
                    if (Document) Document.updateAttachmentList(resp.data.attachments, resp.data.newly_uploaded_id)
                }
            }
        }
        function remove() {
            uploads.update(($uploads) => {
                const i = $uploads.indexOf(upload)
                if (i >= 0) $uploads.splice(i, 1)
                return $uploads
            })
        }
        xhr.upload.addEventListener(
            'load',
            (e) => {
                upload.progress = 100
                remove()
            },
            false,
        )
        xhr.upload.addEventListener(
            'progress',
            (e) => {
                upload.progress = Math.round((e.loaded * 100) / e.total)
            },
            false,
        )
        xhr.upload.addEventListener(
            'error',
            (e) => {
                upload.status = 'error'
                upload.ended = DateTime.now()
                remove()
            },
            false,
        )
        xhr.upload.addEventListener(
            'abort',
            (e) => {
                upload.status = 'aborted'
                upload.ended = DateTime.now()
                remove()
            },
            false,
        )
        // @ts-expect-error
        xhr.open('POST', `/json/cms/1/upload/${Document.docType}/${Document.docId}`, true)
        xhr.setRequestHeader('X-Lg', $lg)
        // @ts-expect-error
        xhr.setRequestHeader('X-Hydria-Token', session.token)
        xhr.setRequestHeader('Accept', 'application/json')
        xhr.send(formData)
    }

    function handleFile(e: any) {
        if (!e.detail.acceptedFiles.length) return

        for (const file of e.detail.acceptedFiles as File[]) {
            setupFileUpload(file)
        }
    }
</script>

<style>
    .btn-site :global(.btn) {
        /* sinon cliquer dedans est fucked up */
        -webkit-user-select: initial !important;
        -moz-user-select: initial !important;
        user-select: initial !important;
    }
    img {
        width: 100%;
        height: 4rem;
        object-fit: contain;
    }

    .link-file {
        display: grid;
        grid-template-columns: 2fr 1fr 5.5rem 1rem 6rem;
        gap: 0.5rem;
        align-items: center;
    }
    .link-image,
    .link-alt {
        display: grid;
        grid-template-columns: 64px 2fr 1fr 5.5rem 5.5rem 1rem 6rem 2rem;
        gap: 0.5rem;
        align-items: center;
    }
    .link-image > *,
    .link-alt > * {
        overflow: hidden;
        text-overflow: ellipsis;
        word-wrap: normal;
    }
    .li-dims,
    .li-view,
    .li-sel {
        justify-self: center;
    }
    .li-size,
    .li-more {
        justify-self: end;
    }
    .link-file:not(:last-child),
    .link-image:not(:last-child),
    .link-alt:not(:last-child) {
        margin-bottom: 0.5rem;
    }

    .maxed {
        max-height: 16rem;
        overflow-y: auto;
    }

    .li-wrap,
    .img-spinner {
        flex: 1 0 0;
        display: grid;
        justify-content: center;
        align-content: center;
    }
    .li-wrap > * {
        grid-row: 1 / 1;
        grid-column: 1 / 1;
    }
    .img-spinner {
        z-index: 1;
    }
</style>

<Modal modalClasses="modal-xl" close={() => close()}>
    <div class="modal-header text-bg-primary">
        <div>{$_('Lien')}</div>
    </div>
    <div class="modal-body d-grid gap-2">
        <div class="d-flex gap-3">
            <div class="col-2 text-right"><label for="link-uri" class="col-form-label">{$_('Lien')}</label></div>
            <div class="col">
                <input class="form-control" id="link-uri" type="text" bind:value={linkUri} placeholder="mailto:... http://... https://..." />
            </div>
        </div>
        <div class="d-flex gap-3">
            <div class="col-2 text-right"><span class="control-span">{$_('Cible')}</span></div>
            <div class="col d-grid gap-2">
                <div aria-hidden="true" onclick={() => (linkTarget = undefined)}><Icon inline icon={linkTarget == undefined ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Ouvrir dans la fenêtre actuelle')}</div>
                <div aria-hidden="true" onclick={() => (linkTarget = '_blank')}><Icon inline icon={linkTarget == '_blank' ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Ouvrir dans une autre fenêtre ou tab')}</div>
            </div>
        </div>
        <div class="d-flex gap-3">
            <div class="col-2 text-right"><span class="control-span">{$_('Apparence')}</span></div>
            <div class="col d-grid cap-2 btn-site">
                <div aria-hidden="true" onclick={() => (linkButton = false)}><Icon inline icon={linkButton == false ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Lien normal')}</div>
                <div aria-hidden="true" onclick={() => (linkButton = true)}><Icon inline icon={linkButton == true ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> <button type="button" class="btn btn-{linkButtonOutline ? 'outline-' : ''}{linkButtonColor}" class:btn-sm={linkButtonSize == 'sm'} class:btn-lg={linkButtonSize == 'lg'}>{$_('Bouton')}</button></div>
            </div>
        </div>
        {#if linkButton}
            <div class="d-flex gap-3">
                <div class="col-2 text-right"><span class="control-span">{$_('Bouton')}</span></div>
                <div class="col d-grid gap-2 align-content-start">
                    <div aria-hidden="true" onclick={() => (linkButtonSize = null)}><Icon inline icon={linkButtonSize == null ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Normal')}</div>
                    <div aria-hidden="true" onclick={() => (linkButtonSize = 'sm')}><Icon inline icon={linkButtonSize == 'sm' ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Petit')}</div>
                    <div aria-hidden="true" onclick={() => (linkButtonSize = 'lg')}><Icon inline icon={linkButtonSize == 'lg' ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Gros')}</div>
                </div>
                <div class="col d-grid gap-2 align-content-start">
                    <div aria-hidden="true" onclick={() => (linkButtonOutline = false)}><Icon inline icon={linkButtonOutline == false ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Plein')}</div>
                    <div aria-hidden="true" onclick={() => (linkButtonOutline = true)}><Icon inline icon={linkButtonOutline == true ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Contour')}</div>
                </div>
                <div class="col d-grid gap-2 align-content-start">
                    <div aria-hidden="true" onclick={() => (linkButtonColor = 'primary')}><Icon inline icon={linkButtonColor == 'primary' ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Primaire')}</div>
                    <div aria-hidden="true" onclick={() => (linkButtonColor = 'secondary')}><Icon inline icon={linkButtonColor == 'secondary' ? 'mdi:radiobox-marked' : 'mdi:radiobox-blank'} /> {$_('Secondaire')}</div>
                </div>
            </div>
        {/if}
    </div>
    {#if $binariesAndUploads.length}
        <div class="modal-body text-bg-secondary pt-2 pb-2">
            <div>{$_('Fichiers liées à ce document')}</div>
        </div>
        <div class="modal-body maxed">
            {#each $binariesAndUploads as att}
                {#if att._type === 'upload'}
                    <div class="link-file text-info">
                        <span class="li-fn"
                            ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            {att.file.name}</span
                        >
                        <span class="li-format">{att.file.type}</span>
                        <span class="li-size"><HumanizedBytes value={att.file.size} /></span>
                        <span class="li-view"><Icon inline icon="mdi:open-in-new" /></span>
                        <span class="li-sel"></span>
                    </div>
                {:else}
                    <div class="link-file">
                        <span class="li-fn">{att.filename}</span>
                        <span class="li-format">{att.mimetype}</span>
                        <span class="li-size"><HumanizedBytes value={att.filesize} /></span>
                        <span class="li-view"><a href={att.url} target="_blank"><Icon inline icon="mdi:open-in-new" /></a></span>
                        <span class="li-sel">
                            <button
                                class="btn btn-sm btn-outline-secondary"
                                onclick={() => {
                                    linkUri = att.url
                                }}>{$_('Sélectionner')}</button
                            >
                        </span>
                    </div>
                {/if}
            {/each}
        </div>
    {/if}
    {#if $imagesAndUploads.length}
        <div class="modal-body text-bg-secondary pt-2 pb-2">
            <div>{$_('Images liées à ce document')}</div>
        </div>
        <div class="modal-body maxed">
            <div class="link-image-grid">
                {#each $imagesAndUploads as att}
                    <div class="link-image">
                        {#if att._type === 'upload'}
                            <span class="li-wrap">
                                <div class="img-spinner">
                                    <div class="spinner-border text-info" role="status">
                                        <span class="visually-hidden">{$_('Traitement…')}.</span>
                                    </div>
                                </div>
                                {#if att.previewImage}
                                    <img class="checkered" src={att.previewImage} alt={att.file.name} />
                                {/if}
                            </span>
                            <span class="li-fn">{att.file.name}</span>
                            <span class="li-format">{att.file.type}</span>
                            <span class="li-dims"></span>
                            <span class="li-size"><HumanizedBytes value={att.file.size} /></span>
                            <span class="li-view"></span>
                            <span class="li-sel"></span>
                            <span class="li-more"></span>
                        {:else}
                            <span class="li-wrap"><img class="checkered" src={selectSrc(att, previewName).url} alt={att.filename} /></span>
                            <span class="li-fn">{att.filename}</span>
                            <span class="li-format">{att.mimetype}</span>
                            <span class="li-dims">{att.image_info.width}×{att.image_info.height}</span>
                            <span class="li-size"><HumanizedBytes value={att.filesize} /></span>
                            <span class="li-view"><a href={att.url} target="_blank"><Icon inline icon="mdi:open-in-new" /></a></span>
                            <span class="li-sel">
                                <button
                                    class="btn btn-sm btn-outline-secondary"
                                    onclick={() => {
                                        linkUri = att.url
                                    }}>{$_('Sélectionner')}</button
                                >
                            </span>
                            <span class="li-more">
                                {#if att._type === 'image' && simpleImageAlts(att, true, true).length}
                                    <button
                                        class="btn btn-sm btn-outline-secondary"
                                        onclick={() => {
                                            linkOpenImage = linkOpenImage == att.id ? null : att.id
                                        }}><Icon inline icon={linkOpenImage == att.id ? 'mdi:chevron-down' : 'mdi:chevron-left'} /></button
                                    >
                                {/if}
                            </span>
                        {/if}
                    </div>
                    {#if att._type === 'image' && linkOpenImage == att.id}
                        <div class="link-image-grid" transition:slide|local={{ duration: 250 }}>
                            {#each simpleImageAlts(att, true, true) as alt}
                                <div class="link-alt">
                                    <span class="li-wrap"></span>
                                    <span class="li-fn">{alt.sizename}</span>
                                    <span class="li-format">{alt.mimetype}</span>
                                    <span class="li-dims">{alt.width}×{alt.height}</span>
                                    <span class="li-size"><HumanizedBytes value={alt.filesize} /></span>
                                    <span class="li-view"><a href={alt.url} target="_blank"><Icon inline icon="mdi:open-in-new" /></a></span>
                                    <span class="li-sel"
                                        ><button
                                            class="btn btn-sm btn-outline-secondary"
                                            onclick={() => {
                                                linkUri = alt.url
                                            }}>{$_('Sélectionner')}</button
                                        ></span
                                    >
                                    <span class="li-more"></span>
                                </div>
                            {/each}
                        </div>
                    {/if}
                {/each}
            </div>
        </div>
    {/if}
    <div class="modal-footer text-bg-light">
        {#if Document}
            <div class="upload-zone">
                <Dropzone on:drop={handleFile} accept={[...$allowedImageMimetypes, ...$allowedBinaryMimetypes].join(',')} multiple={true}>
                    <div class="d-flex gap-2">
                        <div><Icon inline icon="mdi:image-size-select-actual" /></div>
                        <div class="col">
                            {$_('Cliquer ou glisser/déposer des fichiers.')}
                        </div>
                    </div>
                </Dropzone>
            </div>
            <div class="col"></div>
        {/if}
        <button
            type="button"
            class="btn btn-outline-secondary"
            onclick={() => {
                close()
            }}>{$_('Annuler')}</button
        >
        <button type="button" class="btn btn-primary" onclick={() => applyLink()}>{$_('Appliquer')}</button>
    </div>
</Modal>
